import { ChevronLeft, X } from 'lucide-react';
import { Dialog as D } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../cn';

export interface TabBarItem {
  key: string;
  label: string;
  icon: ReactNode;
  active?: boolean;
  badge?: number;
  onSelect: () => void;
}

/**
 * Phone layout for the owner PWA: large title bar, scrolling content, bottom tab
 * bar within thumb reach. Respects safe areas.
 */
export function MobileShell({
  title,
  onBack,
  actions,
  tabs,
  children,
  largeTitle = true,
}: {
  title: ReactNode;
  onBack?: () => void;
  actions?: ReactNode;
  tabs?: TabBarItem[];
  children: ReactNode;
  largeTitle?: boolean;
}) {
  return (
    <div className="floor flex min-h-dvh flex-col text-ink">
      <header className="sticky top-0 z-30 border-b border-line/70 bg-canvas/90 pt-[env(safe-area-inset-top)] backdrop-blur-md">
        <div className="flex h-12 items-center gap-1 px-2">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="flex h-11 items-center gap-0.5 rounded-md pr-2 text-md text-primary active:opacity-60"
            >
              <ChevronLeft className="size-6" />
              Назад
            </button>
          ) : (
            <span className="w-2" />
          )}
          <div className="min-w-0 flex-1 truncate text-center text-md font-semibold">
            {/* With a large title below, the compact one is for screen readers only. */}
            <span className={cn(largeTitle && !onBack && 'sr-only')}>{title}</span>
          </div>
          <div className="flex min-w-11 items-center justify-end gap-1">{actions}</div>
        </div>
        {largeTitle && !onBack && (
          <h1 className="px-4 pb-3 text-3xl font-bold tracking-[-0.02em]">{title}</h1>
        )}
      </header>
      <main
        className={cn(
          'flex-1 px-4 pt-4',
          tabs ? 'pb-[calc(5.5rem+env(safe-area-inset-bottom))]' : 'pb-8',
        )}
      >
        {children}
      </main>
      {tabs && (
        <nav
          aria-label="Разделы"
          className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md"
        >
          <ul className="mx-auto flex max-w-lg">
            {tabs.map((t) => (
              <li key={t.key} className="flex-1">
                <button
                  type="button"
                  onClick={t.onSelect}
                  aria-current={t.active ? 'page' : undefined}
                  className={cn(
                    'relative flex h-16 w-full flex-col items-center justify-center gap-1 text-2xs font-medium',
                    t.active ? 'text-primary' : 'text-ink-3 active:text-ink',
                  )}
                >
                  <span className="[&_svg]:size-6">{t.icon}</span>
                  {t.label}
                  {t.badge ? (
                    <span className="absolute top-2 left-1/2 ml-2 min-w-4 rounded-full bg-danger px-1 text-center text-2xs leading-4 text-white tnum">
                      {t.badge}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}

/** Grouped list rows, iOS-settings style, for phone screens. */
export function ListGroup({
  title,
  children,
  className,
}: {
  title?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('mb-6', className)}>
      {title && <h2 className="mb-2 px-1 text-sm font-medium text-ink-3">{title}</h2>}
      <div className="overflow-hidden rounded-lg border border-line bg-surface">{children}</div>
    </section>
  );
}

export function ListRow({
  title,
  subtitle,
  leading,
  trailing,
  onClick,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
}) {
  const inner = (
    <>
      {leading && <div className="shrink-0">{leading}</div>}
      <div className="min-w-0 flex-1 text-left">
        <div className="truncate text-md text-ink">{title}</div>
        {subtitle && <div className="truncate text-sm text-ink-3">{subtitle}</div>}
      </div>
      {trailing && <div className="shrink-0 text-right">{trailing}</div>}
    </>
  );
  const cls =
    'flex min-h-14 w-full items-center gap-3 border-b border-line px-4 py-2.5 last:border-b-0';
  return onClick ? (
    <button type="button" onClick={onClick} className={cn(cls, 'active:bg-sunken')}>
      {inner}
    </button>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

/**
 * Phone bottom sheet: rises from the bottom edge, rounded top, scrolls inside,
 * footer stays above the home indicator. Swipe-free by design (a button closes it).
 */
export function BottomSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  full,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Takes the whole screen height (forms with a keyboard, scanners). */
  full?: boolean;
}) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-ink/25 animate-fade-in" />
        <D.Content
          // Phones: no keyboard or focus ring popping up on open; forms focus their own field.
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 mx-auto flex w-full max-w-lg flex-col rounded-t-xl bg-surface shadow-dialog animate-rise-in focus:outline-none',
            full ? 'h-[calc(100dvh-env(safe-area-inset-top)-12px)]' : 'max-h-[88dvh]',
          )}
        >
          <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-line-strong" aria-hidden />
          <div className="flex items-start gap-3 px-4 pt-2 pb-3">
            <div className="min-w-0 flex-1">
              <D.Title className="text-lg font-semibold">{title}</D.Title>
              {description ? (
                <D.Description className="mt-0.5 text-sm text-ink-3">{description}</D.Description>
              ) : (
                <D.Description className="sr-only">{typeof title === 'string' ? title : ''}</D.Description>
              )}
            </div>
            <D.Close
              className="-mr-1 flex size-11 items-center justify-center rounded-full text-ink-3 active:bg-rail"
              aria-label="Закрыть"
            >
              <X className="size-5" />
            </D.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">{children}</div>
          {footer && (
            <div className="flex gap-2 border-t border-line px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
              {footer}
            </div>
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
